'use client';

import {
  Alert,
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Provider, SecurityGroupRuleProtocol } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { BorderlessTableCell } from '@/components/table/components';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { Regions } from '@/data/inventory/regions';

import type {
  AddPrivateLinkWizardState,
  DisplayProtocol,
  SecurityGroupRuleState,
} from '../addPrivateLinkWizardState';

interface RegionOption {
  value: string;
  label: string;
}

const AWS_REGION_OPTIONS: RegionOption[] = Object.entries(
  Regions[Provider.Aws]
).map(([code, { displayName }]) => ({
  value: code,
  label: `${code} - ${displayName}`,
}));

const VPCE_SERVICE_NAME_REGEX =
  /^com\.amazonaws\.vpce\.[a-z]{2}(-[a-z]+)+-\d\.vpce-svc-[0-9a-f]{17}$/;

const FQDN_REGEX =
  /^(?!-)[a-zA-Z0-9-]{1,63}(?<!-)(\.[a-zA-Z0-9-]{1,63})*\.[a-zA-Z]{2,}$/;

const PROTOCOL_OPTIONS: { value: DisplayProtocol; label: string }[] = [
  { value: 'TCP', label: 'TCP' },
  { value: 'UDP', label: 'UDP' },
  { value: 'ALL_TCP', label: 'All TCP' },
  { value: 'ALL_UDP', label: 'All UDP' },
  { value: 'ALL_TRAFFIC', label: 'All Traffic' },
];

const DISPLAY_PROTOCOL_SET = new Set<string>(
  PROTOCOL_OPTIONS.map((p) => p.value)
);

const isDisplayProtocol = (value: string): value is DisplayProtocol =>
  DISPLAY_PROTOCOL_SET.has(value);

const ALL_PORTS = { fromPort: 0, toPort: 65535 };

function isPortsLocked(dp: DisplayProtocol): boolean {
  return dp === 'ALL_TCP' || dp === 'ALL_UDP' || dp === 'ALL_TRAFFIC';
}

const emptyRule: SecurityGroupRuleState = {
  protocol: 'TCP',
  fromPort: 443,
  toPort: 443,
  displayProtocol: 'TCP',
};

export const AddPrivateLinkStep = (
  props: StepProps<AddPrivateLinkWizardState>
) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = state.mode === 'edit';

  const update = (partial: Partial<AddPrivateLinkWizardState>) =>
    setWizardState({ ...state, ...partial });

  const updateRule = (
    index: number,
    partial: Partial<SecurityGroupRuleState>
  ) => {
    const rules = [...state.securityGroupRules];
    rules[index] = { ...rules[index], ...partial };
    update({ securityGroupRules: rules });
  };

  const handleProtocolChange = (index: number, dp: DisplayProtocol) => {
    switch (dp) {
      case 'ALL_TCP':
        updateRule(index, {
          protocol: 'TCP',
          ...ALL_PORTS,
          displayProtocol: dp,
        });
        break;
      case 'ALL_UDP':
        updateRule(index, {
          protocol: 'UDP',
          ...ALL_PORTS,
          displayProtocol: dp,
        });
        break;
      case 'ALL_TRAFFIC':
        updateRule(index, {
          protocol: 'ALL',
          ...ALL_PORTS,
          displayProtocol: dp,
        });
        break;
      case 'TCP':
        updateRule(index, {
          protocol: 'TCP',
          fromPort: 443,
          toPort: 443,
          displayProtocol: dp,
        });
        break;
      case 'UDP':
        updateRule(index, {
          protocol: 'UDP',
          fromPort: 443,
          toPort: 443,
          displayProtocol: dp,
        });
        break;
    }
  };

  const removeRule = (index: number) => {
    const rules = state.securityGroupRules.filter((_, i) => i !== index);
    update({ securityGroupRules: rules });
  };

  const addRule = () => {
    update({
      securityGroupRules: [...state.securityGroupRules, { ...emptyRule }],
    });
  };

  const selectedRegion = useMemo(
    () => AWS_REGION_OPTIONS.find((o) => o.value === state.region) ?? null,
    [state.region]
  );

  const hasInvalidPorts = useMemo(
    () =>
      state.securityGroupRules.some(
        (r) =>
          r.fromPort < 0 ||
          r.fromPort > 65535 ||
          r.toPort < 0 ||
          r.toPort > 65535 ||
          r.fromPort > r.toPort
      ),
    [state.securityGroupRules]
  );

  const hasOverlappingPortRanges = useMemo(() => {
    const protocolsConflict = (a: string, b: string) =>
      a === b || a === 'ALL' || b === 'ALL';

    const rules = state.securityGroupRules;
    for (let i = 0; i < rules.length; i++) {
      for (let j = i + 1; j < rules.length; j++) {
        if (
          protocolsConflict(rules[i].protocol, rules[j].protocol) &&
          rules[i].fromPort <= rules[j].toPort &&
          rules[j].fromPort <= rules[i].toPort
        ) {
          return true;
        }
      }
    }
    return false;
  }, [state.securityGroupRules]);

  const isValidServiceName =
    isEditing || VPCE_SERVICE_NAME_REGEX.test(state.serviceName);
  const isValidFqdn =
    state.fqdn.trim().length > 0 && FQDN_REGEX.test(state.fqdn.trim());
  const canSubmit = isEditing
    ? isValidFqdn &&
      !submitting &&
      !hasOverlappingPortRanges &&
      !hasInvalidPorts
    : isValidServiceName &&
      state.region.trim().length > 0 &&
      isValidFqdn &&
      !submitting &&
      !hasOverlappingPortRanges &&
      !hasInvalidPorts;

  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const WIRE_PROTOCOL: Record<string, SecurityGroupRuleProtocol> = {
        TCP: SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_TCP,
        UDP: SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_UDP,
        ALL: SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_ALL,
      };
      const sgRules = state.securityGroupRules.map((r) => ({
        protocol:
          WIRE_PROTOCOL[r.protocol] ??
          SecurityGroupRuleProtocol.SECURITY_GROUP_RULE_PROTOCOL_TCP,
        fromPort: r.fromPort,
        toPort: r.toPort,
      }));

      if (isEditing && state.editingPrivateLinkId) {
        await dal.lakehouse.updatePrivateLink(
          state.workspaceId,
          state.editingPrivateLinkId,
          {
            fqdn: state.fqdn,
            securityGroupRules: sgRules,
          }
        );
        void props.finish('PrivateLink endpoint updated successfully');
      } else {
        await dal.lakehouse.createPrivateLink(state.workspaceId, {
          serviceName: state.serviceName,
          region: state.region,
          fqdn: state.fqdn,
          securityGroupRules: sgRules,
        });
        void props.finish('PrivateLink endpoint created successfully');
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEditing ? 'update' : 'create'} PrivateLink endpoint`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = isEditing ? 'Save' : 'Create';
  const submittingLabel = isEditing ? 'Saving...' : 'Creating...';

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canSubmit}
      nextButtonText={submitting ? submittingLabel : submitLabel}
      nextButtonLoadingText={submittingLabel}
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleSubmit}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Box paddingX='min(100px, 6vw)'>
        <Stack gap='24px' maxWidth='540px'>
          <Stack gap='8px'>
            <Typography variant='h6'>PrivateLink Endpoint</Typography>
            <Typography variant='body2' color='text.secondary'>
              {isEditing
                ? 'Update the FQDN and security group rules for this PrivateLink endpoint.'
                : 'Configure an AWS PrivateLink endpoint for private connectivity to your data lake.'}
            </Typography>
          </Stack>

          <FormControl fullWidth>
            <FormLabel>
              <Typography variant='subtitle1'>Service Name</Typography>
            </FormLabel>
            <TextField
              size='small'
              placeholder='com.amazonaws.vpce.us-east-1.vpce-svc-0123456789abcdef0'
              value={state.serviceName}
              onChange={(e) => update({ serviceName: e.target.value })}
              error={
                !isEditing &&
                state.serviceName.length > 0 &&
                !VPCE_SERVICE_NAME_REGEX.test(state.serviceName)
              }
              helperText={
                !isEditing &&
                state.serviceName.length > 0 &&
                !VPCE_SERVICE_NAME_REGEX.test(state.serviceName)
                  ? 'Must be a valid VPC endpoint service name (e.g. com.amazonaws.vpce.us-east-1.vpce-svc-0123456789abcdef0)'
                  : undefined
              }
              disabled={isEditing}
              fullWidth
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>
              <Typography variant='subtitle1'>Region</Typography>
            </FormLabel>
            <Autocomplete
              options={AWS_REGION_OPTIONS}
              value={selectedRegion}
              onChange={(_event, newValue) =>
                update({ region: newValue?.value ?? '' })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              disabled={isEditing}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  placeholder='Search regions...'
                />
              )}
            />
            {state.region &&
              state.workspaceRegion &&
              state.region !== state.workspaceRegion && (
                <Alert severity='warning' sx={{ marginTop: '8px' }}>
                  <Typography variant='body2'>
                    The selected region differs from your workspace region (
                    {state.workspaceRegion}). Cross-region PrivateLink incurs
                    cross-region data transfer costs.
                  </Typography>
                </Alert>
              )}
          </FormControl>

          <FormControl fullWidth>
            <FormLabel>
              <Typography variant='subtitle1'>FQDN</Typography>
            </FormLabel>
            <TextField
              size='small'
              placeholder='my-service.example.com'
              value={state.fqdn}
              onChange={(e) => update({ fqdn: e.target.value })}
              error={
                state.fqdn.length > 0 && !FQDN_REGEX.test(state.fqdn.trim())
              }
              helperText={
                state.fqdn.length > 0 && !FQDN_REGEX.test(state.fqdn.trim())
                  ? 'Must be a valid fully qualified domain name (e.g. my-service.example.com)'
                  : undefined
              }
              fullWidth
            />
          </FormControl>

          <Stack gap='12px'>
            <Typography variant='subtitle1'>Security Group Rules</Typography>
            <Typography variant='body2' color='text.secondary'>
              Define the allowed traffic from your workloads to your private
              endpoints.
            </Typography>
            {state.securityGroupRules.length > 0 && (
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableBody>
                  <TableRow sx={{ height: '32px' }}>
                    <BorderlessTableCell sx={{ width: '40px' }} />
                    <BorderlessTableCell sx={{ paddingRight: '12px' }}>
                      <Typography variant='body2' color='text.secondary'>
                        Protocol
                      </Typography>
                    </BorderlessTableCell>
                    <BorderlessTableCell sx={{ paddingRight: '12px' }}>
                      <Typography variant='body2' color='text.secondary'>
                        From Port
                      </Typography>
                    </BorderlessTableCell>
                    <BorderlessTableCell>
                      <Typography variant='body2' color='text.secondary'>
                        To Port
                      </Typography>
                    </BorderlessTableCell>
                  </TableRow>
                  {state.securityGroupRules.map((rule, index) => {
                    const dp = rule.displayProtocol;
                    const locked = isPortsLocked(dp);
                    return (
                      <TableRow key={index} sx={{ height: '48px' }}>
                        <BorderlessTableCell sx={{ width: '40px' }}>
                          <IconButton
                            size='small'
                            onClick={() => removeRule(index)}
                          >
                            <i
                              className='material-symbols-close-rounded'
                              style={{ fontSize: '18px' }}
                            />
                          </IconButton>
                        </BorderlessTableCell>
                        <BorderlessTableCell sx={{ paddingRight: '12px' }}>
                          <Select
                            size='small'
                            fullWidth
                            value={dp}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (isDisplayProtocol(v))
                                handleProtocolChange(index, v);
                            }}
                          >
                            {PROTOCOL_OPTIONS.map((p) => (
                              <MenuItem key={p.value} value={p.value}>
                                {p.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </BorderlessTableCell>
                        <BorderlessTableCell sx={{ paddingRight: '12px' }}>
                          <TextField
                            size='small'
                            fullWidth
                            disabled={locked}
                            value={rule.fromPort}
                            error={
                              !locked &&
                              (rule.fromPort < 0 || rule.fromPort > 65535)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d+$/.test(val)) {
                                updateRule(index, {
                                  fromPort: val === '' ? 0 : parseInt(val, 10),
                                });
                              }
                            }}
                            slotProps={{
                              htmlInput: { inputMode: 'numeric' },
                            }}
                          />
                        </BorderlessTableCell>
                        <BorderlessTableCell>
                          <TextField
                            size='small'
                            fullWidth
                            disabled={locked}
                            value={rule.toPort}
                            error={
                              !locked &&
                              (rule.toPort < 0 || rule.toPort > 65535)
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || /^\d+$/.test(val)) {
                                updateRule(index, {
                                  toPort: val === '' ? 0 : parseInt(val, 10),
                                });
                              }
                            }}
                            slotProps={{
                              htmlInput: { inputMode: 'numeric' },
                            }}
                          />
                        </BorderlessTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {hasOverlappingPortRanges && (
              <FormHelperText error>
                Port ranges with the same protocol must not overlap.
              </FormHelperText>
            )}
            {hasInvalidPorts && (
              <FormHelperText error>
                Port values must be between 0 and 65535, and From Port must not
                exceed To Port.
              </FormHelperText>
            )}
            <IconButton
              size='small'
              onClick={addRule}
              sx={{ width: 'fit-content' }}
            >
              <i
                className='material-symbols-add-rounded'
                style={{ fontSize: '18px' }}
              />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
